import { createContext, useContext, useState, useEffect } from 'react';

// Translation strings
const translations = {
  en: {
    // Brand
    brand_name: "Voltea Ideas",
    brand_tagline: "Turn challenges into structured ideas, business models, and ready-to-pitch concepts powered by AI.",
    
    // Navigation
    nav_get_started: "Get Started",
    nav_login: "Login",
    nav_logout: "Sign Out",
    nav_save: "Save",
    nav_home: "Home",
    nav_previous: "Previous",
    nav_next: "Next",
    
    // Landing Page
    landing_hero_title: "Transform Ideas into",
    landing_hero_title_highlight: "Pitch-Ready",
    landing_hero_title_end: "Concepts",
    landing_hero_description: "An AI-powered innovation platform that takes you from problem to presentation. Define challenges, explore ideas, build business models, and create compelling pitches — all in one guided flow.",
    landing_start_innovating: "Start",
    landing_cta_title: "Ready to Launch Your Next Big Idea?",
    landing_cta_description: "Join entrepreneurs, students, and innovation teams who are transforming their challenges into pitch-ready concepts with AI.",
    landing_cta_button: "Get Started Free",
    landing_footer_powered: "Powered by Emergent",
    
    // Features
    feature_define_title: "Define Your Challenge",
    feature_define_desc: "Start with any problem, challenge, or opportunity. Our AI understands natural language.",
    feature_explore_title: "Explore Ideas",
    feature_explore_desc: "AI generates a visual conceptual map with diverse, innovative approaches.",
    feature_canvas_title: "Build Your Canvas",
    feature_canvas_desc: "Transform ideas into Value Proposition and Business Model Canvases automatically.",
    feature_concept_title: "Create Your Concept",
    feature_concept_desc: "Get a pitch-ready creative concept with clear differentiation and target audience.",
    feature_prototype_title: "Generate Prototypes",
    feature_prototype_desc: "Receive optimized prompts for creating visual prototypes and interfaces.",
    feature_pitch_title: "Pitch Perfect",
    feature_pitch_desc: "Export professional presentations and compelling elevator pitches.",
    
    // Steps
    step_describe: "Describe your problem",
    step_explore: "Explore AI-generated ideas",
    step_build: "Build your business model",
    step_pitch: "Pitch your concept",
    
    // Dashboard
    dashboard_title: "Your Projects",
    dashboard_subtitle: "Transform challenges into pitch-ready concepts",
    dashboard_new_project: "New Project",
    dashboard_no_projects: "No projects yet",
    dashboard_no_projects_desc: "Start your innovation journey by creating your first project. Define a problem and let AI help you build a pitch-ready concept.",
    dashboard_create_first: "Create Your First Project",
    dashboard_delete: "Delete",
    dashboard_step: "Step",
    
    // Dialog
    dialog_create_title: "Create New Project",
    dialog_create_desc: "Give your innovation project a name to get started.",
    dialog_create_placeholder: "e.g., Sustainable Food Delivery",
    dialog_cancel: "Cancel",
    dialog_create: "Create Project",
    
    // Wizard Steps - NEW ORDER: Problem > Ideas > Concept > VPC > BMC > Prototype > Pitch
    wizard_problem: "Problem",
    wizard_ideas: "Ideas",
    wizard_concept: "Concept",
    wizard_value_prop: "Value Prop",
    wizard_business_model: "Business Model",
    wizard_prototype: "Prototype",
    wizard_pitch: "Pitch",
    
    // Problem Step
    problem_title: "Define Your Challenge",
    problem_description: "Describe the problem, challenge, or opportunity you want to explore. Be as detailed as you like — AI will help shape it into ideas.",
    problem_tips_title: "Tips for better results",
    problem_tip_1: "Start with \"How might we...\" for open-ended challenges",
    problem_tip_2: "Include context about who experiences this problem",
    problem_tip_3: "Mention any constraints or requirements",
    problem_save_draft: "Save Draft",
    problem_generate: "Generate Ideas",
    
    // Idea Map Step
    ideamap_title: "Idea Map",
    ideamap_description: "Explore AI-generated ideas and select one to develop further",
    ideamap_generate: "Generate Ideas",
    ideamap_regenerate: "Regenerate",
    ideamap_regenerate_hint: "Regenerating will create 12 new alternative ideas",
    ideamap_continue: "Continue with Idea",
    ideamap_ready_title: "Ready to Generate Ideas",
    ideamap_ready_desc: "Click \"Generate Ideas\" to let AI create a conceptual map based on your problem statement.",
    ideamap_selected: "Selected Idea",
    ideamap_generating: "Generating...",
    ideamap_likes: "likes",
    ideamap_export_pdf: "Export Ideas PDF",
    
    // Concept Step (NOW STEP 3)
    concept_title: "Creative Concept",
    concept_description: "Your pitch-ready concept with clear positioning - the foundation for your canvases",
    concept_generate: "Generate Concept",
    concept_regenerate: "Regenerate",
    concept_continue: "Continue to Value Proposition",
    concept_name: "Concept Name",
    concept_problem_solved: "Problem Solved",
    concept_solution: "Proposed Solution",
    concept_differentiation: "Key Differentiation",
    concept_audience: "Target Audience",
    concept_benefits: "Key Benefits",
    concept_export_pdf: "Export Concept PDF",
    
    // Value Proposition Step (NOW STEP 4)
    vpc_title: "Value Proposition Canvas",
    vpc_description: "Define customer needs and how your concept addresses them",
    vpc_generate: "Generate Canvas",
    vpc_regenerate: "Regenerate",
    vpc_continue: "Continue to Business Model",
    vpc_customer_profile: "Customer Profile",
    vpc_value_map: "Value Map",
    vpc_jobs: "Customer Jobs",
    vpc_pains: "Pains",
    vpc_gains: "Gains",
    vpc_products: "Products & Services",
    vpc_pain_relievers: "Pain Relievers",
    vpc_gain_creators: "Gain Creators",
    vpc_add_item: "Add item...",
    vpc_export_pdf: "Export VPC PDF",
    
    // Viability Indicators
    viability_title: "Viability Assessment",
    viability_project: "Project Viability",
    viability_technical: "Technical Viability",
    viability_business: "Business Viability",
    viability_market: "Market Feasibility",
    viability_high: "High",
    viability_medium: "Medium",
    viability_low: "Low",
    need_relevance_title: "Need Relevance",
    need_problem: "Problem Addressed",
    need_urgency: "Why It Matters",
    
    // Business Model Step (NOW STEP 5)
    bmc_title: "Business Model Canvas",
    bmc_description: "Define how your concept creates, delivers, and captures value",
    bmc_generate: "Generate Canvas",
    bmc_regenerate: "Regenerate",
    bmc_continue: "Create Prototype Prompt",
    bmc_partners: "Key Partners",
    bmc_activities: "Key Activities",
    bmc_resources: "Key Resources",
    bmc_value_props: "Value Propositions",
    bmc_relationships: "Customer Relationships",
    bmc_channels: "Channels",
    bmc_segments: "Customer Segments",
    bmc_costs: "Cost Structure",
    bmc_revenue: "Revenue Streams",
    bmc_export_pdf: "Export BMC PDF",
    
    // Prototype Step (NOW STEP 6)
    prototype_title: "Prototype Prompt",
    prototype_description: "Generate a prompt for visual prototyping tools",
    prototype_generate: "Generate Prompt",
    prototype_regenerate: "Regenerate",
    prototype_continue: "Create Presentation",
    prototype_copy: "Copy",
    prototype_copied: "Copied!",
    prototype_context: "Product Context",
    prototype_user: "Target User",
    prototype_features: "Key Features",
    prototype_style: "Visual Style",
    prototype_tools: "Suggested Tools",
    prototype_export_pdf: "Export Prompt PDF",
    
    // Presentation Step (NOW STEP 7)
    presentation_title: "Presentation & Pitch",
    presentation_description: "Your complete pitch deck and elevator pitch",
    presentation_generate: "Generate Presentation",
    presentation_regenerate: "Regenerate",
    presentation_export: "Export PDF",
    presentation_exporting: "Exporting...",
    presentation_slides: "Presentation Slides",
    presentation_pitch: "Elevator Pitch",
    presentation_pitch_duration: "~30-45 seconds when spoken naturally",
    presentation_complete_title: "Congratulations!",
    presentation_complete_desc: "You've completed your innovation journey. Export your presentation and pitch to share with others.",
    presentation_copied_slide: "Slide copied to clipboard",
    completion_finalize_button: "Finalize Project",
    completion_finalized: "Project successfully saved",
    
    // Common
    common_loading: "Loading...",
    common_saving: "Saving...",
    common_creating: "Creating...",
    common_generating: "Generating...",
    common_success: "Success",
    common_error: "Error",
    common_add: "Add",
    auth_login_title: "Sign in to your account",
    auth_login_subtitle: "Use email and password or continue with Google",
    auth_signup_title: "Create your account",
    auth_signup_subtitle: "Start turning challenges into pitch-ready concepts",
    auth_email: "Email",
    auth_password: "Password",
    auth_confirm_password: "Confirm Password",
    auth_login_button: "Login",
    auth_signup_button: "Sign Up",
    auth_login_with_google: "Continue with Google",
    auth_or: "or",
    auth_no_account: "Don't have an account?",
    auth_have_account: "Already have an account?",
    auth_signup_link: "Sign up",
    auth_login_link: "Log in",
    auth_password_mismatch: "Passwords do not match",
  },
  es: {
    // Brand
    brand_name: "Voltea Ideas",
    brand_tagline: "Convierte desafíos en ideas estructuradas, modelos de negocio y conceptos listos para presentar con IA.",
    
    // Navigation
    nav_get_started: "Comenzar",
    nav_login: "Iniciar Sesión",
    nav_logout: "Cerrar Sesión",
    nav_save: "Guardar",
    nav_home: "Inicio",
    nav_previous: "Anterior",
    nav_next: "Siguiente",
    
    // Landing Page
    landing_hero_title: "Transforma Ideas en",
    landing_hero_title_highlight: "Conceptos",
    landing_hero_title_end: "Listos para Pitch",
    landing_hero_description: "Una plataforma de innovación impulsada por IA que te lleva del problema a la presentación. Define desafíos, explora ideas, construye modelos de negocio y crea pitches convincentes — todo en un flujo guiado.",
    landing_start_innovating: "Comenzar",
    landing_cta_title: "¿Listo para Lanzar tu Próxima Gran Idea?",
    landing_cta_description: "Únete a emprendedores, estudiantes y equipos de innovación que están transformando sus desafíos en conceptos listos para presentar con IA.",
    landing_cta_button: "Comenzar Gratis",
    landing_footer_powered: "Desarrollado por Emergent",
    
    // Features
    feature_define_title: "Define tu Desafío",
    feature_define_desc: "Comienza con cualquier problema, desafío u oportunidad. Nuestra IA entiende lenguaje natural.",
    feature_explore_title: "Explora Ideas",
    feature_explore_desc: "La IA genera un mapa conceptual visual con enfoques diversos e innovadores.",
    feature_canvas_title: "Construye tu Canvas",
    feature_canvas_desc: "Transforma ideas en Canvas de Propuesta de Valor y Modelo de Negocio automáticamente.",
    feature_concept_title: "Crea tu Concepto",
    feature_concept_desc: "Obtén un concepto creativo listo para presentar con diferenciación clara y audiencia objetivo.",
    feature_prototype_title: "Genera Prototipos",
    feature_prototype_desc: "Recibe prompts optimizados para crear prototipos visuales e interfaces.",
    feature_pitch_title: "Pitch Perfecto",
    feature_pitch_desc: "Exporta presentaciones profesionales y elevator pitches convincentes.",
    
    // Steps
    step_describe: "Describe tu problema",
    step_explore: "Explora ideas generadas por IA",
    step_build: "Construye tu modelo de negocio",
    step_pitch: "Presenta tu concepto",
    
    // Dashboard
    dashboard_title: "Tus Proyectos",
    dashboard_subtitle: "Transforma desafíos en conceptos listos para presentar",
    dashboard_new_project: "Nuevo Proyecto",
    dashboard_no_projects: "Aún no hay proyectos",
    dashboard_no_projects_desc: "Comienza tu viaje de innovación creando tu primer proyecto. Define un problema y deja que la IA te ayude a construir un concepto listo para presentar.",
    dashboard_create_first: "Crea tu Primer Proyecto",
    dashboard_delete: "Eliminar",
    dashboard_step: "Paso",
    
    // Dialog
    dialog_create_title: "Crear Nuevo Proyecto",
    dialog_create_desc: "Dale un nombre a tu proyecto de innovación para comenzar.",
    dialog_create_placeholder: "ej., Entrega de Comida Sostenible",
    dialog_cancel: "Cancelar",
    dialog_create: "Crear Proyecto",
    
    // Wizard Steps - NEW ORDER
    wizard_problem: "Problema",
    wizard_ideas: "Ideas",
    wizard_concept: "Concepto",
    wizard_value_prop: "Prop. Valor",
    wizard_business_model: "Modelo Neg.",
    wizard_prototype: "Prototipo",
    wizard_pitch: "Pitch",
    
    // Problem Step
    problem_title: "Define tu Desafío",
    problem_description: "Describe el problema, desafío u oportunidad que quieres explorar. Sé tan detallado como desees — la IA te ayudará a convertirlo en ideas.",
    problem_tips_title: "Consejos para mejores resultados",
    problem_tip_1: "Comienza con \"¿Cómo podríamos...\" para desafíos abiertos",
    problem_tip_2: "Incluye contexto sobre quién experimenta este problema",
    problem_tip_3: "Menciona cualquier restricción o requisito",
    problem_save_draft: "Guardar Borrador",
    problem_generate: "Generar Ideas",
    
    // Idea Map Step
    ideamap_title: "Mapa de Ideas",
    ideamap_description: "Explora ideas generadas por IA y selecciona una para desarrollar",
    ideamap_generate: "Generar Ideas",
    ideamap_regenerate: "Regenerar",
    ideamap_regenerate_hint: "Regenerar creará 12 nuevas ideas alternativas",
    ideamap_continue: "Continuar con Idea",
    ideamap_ready_title: "Listo para Generar Ideas",
    ideamap_ready_desc: "Haz clic en \"Generar Ideas\" para que la IA cree un mapa conceptual basado en tu declaración del problema.",
    ideamap_selected: "Idea Seleccionada",
    ideamap_generating: "Generando...",
    ideamap_likes: "me gusta",
    ideamap_favorite_hint: "Haz doble clic en una idea para marcarla como favorita",
    
    // Concept Step (NOW STEP 3)
    concept_title: "Concepto Creativo",
    concept_description: "Tu concepto listo para presentar - la base para tus canvas",
    concept_generate: "Generar Concepto",
    concept_regenerate: "Regenerar",
    concept_continue: "Continuar a Propuesta de Valor",
    concept_name: "Nombre del Concepto",
    concept_problem_solved: "Problema Resuelto",
    concept_solution: "Solución Propuesta",
    concept_differentiation: "Diferenciación Clave",
    concept_audience: "Audiencia Objetivo",
    concept_benefits: "Beneficios Clave",
    
    // Value Proposition Step (NOW STEP 4)
    vpc_title: "Canvas de Propuesta de Valor",
    vpc_description: "Define las necesidades del cliente y cómo tu concepto las aborda",
    vpc_generate: "Generar Canvas",
    vpc_regenerate: "Regenerar",
    vpc_continue: "Continuar al Modelo de Negocio",
    vpc_customer_profile: "Perfil del Cliente",
    vpc_value_map: "Mapa de Valor",
    vpc_jobs: "Trabajos del Cliente",
    vpc_pains: "Dolores",
    vpc_gains: "Ganancias",
    vpc_products: "Productos y Servicios",
    vpc_pain_relievers: "Aliviadores de Dolor",
    vpc_gain_creators: "Creadores de Ganancia",
    vpc_add_item: "Agregar elemento...",
    
    // Viability Indicators
    viability_title: "Evaluación de Viabilidad",
    viability_project: "Viabilidad del Proyecto",
    viability_technical: "Viabilidad Técnica",
    viability_business: "Viabilidad de Negocio",
    viability_market: "Factibilidad de Mercado",
    viability_high: "Alta",
    viability_medium: "Media",
    viability_low: "Baja",
    need_relevance_title: "Relevancia de la Necesidad",
    need_problem: "Problema Abordado",
    need_urgency: "Por Qué Importa",
    
    // Business Model Step (NOW STEP 5)
    bmc_title: "Canvas de Modelo de Negocio",
    bmc_description: "Define cómo tu concepto crea, entrega y captura valor",
    bmc_generate: "Generar Canvas",
    bmc_regenerate: "Regenerar",
    bmc_continue: "Crear Prompt de Prototipo",
    bmc_partners: "Socios Clave",
    bmc_activities: "Actividades Clave",
    bmc_resources: "Recursos Clave",
    bmc_value_props: "Propuestas de Valor",
    bmc_relationships: "Relaciones con Clientes",
    bmc_channels: "Canales",
    bmc_segments: "Segmentos de Clientes",
    bmc_costs: "Estructura de Costos",
    bmc_revenue: "Fuentes de Ingresos",
    
    // Prototype Step (NOW STEP 6)
    prototype_title: "Prompt de Prototipo",
    prototype_description: "Genera un prompt para herramientas de prototipado visual",
    prototype_generate: "Generar Prompt",
    prototype_regenerate: "Regenerar",
    prototype_continue: "Crear Presentación",
    prototype_copy: "Copiar",
    prototype_copied: "¡Copiado!",
    prototype_context: "Contexto del Producto",
    prototype_user: "Usuario Objetivo",
    prototype_features: "Características Clave",
    prototype_style: "Estilo Visual",
    prototype_tools: "Herramientas Sugeridas",
    
    // Presentation Step (NOW STEP 7)
    presentation_title: "Presentación y Pitch",
    presentation_description: "Tu pitch deck completo y elevator pitch",
    presentation_generate: "Generar Presentación",
    presentation_regenerate: "Regenerar",
    presentation_slides: "Diapositivas de Presentación",
    presentation_pitch: "Elevator Pitch",
    presentation_pitch_duration: "~30-45 segundos al hablar naturalmente",
    presentation_complete_title: "¡Felicitaciones!",
    presentation_complete_desc: "Has completado tu viaje de innovación. Exporta tu presentación y pitch para compartir con otros.",
    presentation_copied_slide: "Diapositiva copiada al portapapeles",
    completion_finalize_button: "Finalizar proyecto",
    completion_finalized: "Proyecto guardado correctamente",
    
    // Common
    common_loading: "Cargando...",
    common_saving: "Guardando...",
    common_creating: "Creando...",
    common_generating: "Generando...",
    common_success: "Éxito",
    common_error: "Error",
    common_add: "Agregar",
    auth_login_title: "Inicia sesión en tu cuenta",
    auth_login_subtitle: "Usa correo y contraseña o continúa con Google",
    auth_signup_title: "Crea tu cuenta",
    auth_signup_subtitle: "Comienza a transformar desafíos en conceptos listos para presentar",
    auth_email: "Correo electrónico",
    auth_password: "Contraseña",
    auth_confirm_password: "Confirmar contraseña",
    auth_login_button: "Iniciar sesión",
    auth_signup_button: "Registrarse",
    auth_login_with_google: "Continuar con Google",
    auth_or: "o",
    auth_no_account: "¿No tienes cuenta?",
    auth_have_account: "¿Ya tienes cuenta?",
    auth_signup_link: "Regístrate",
    auth_login_link: "Inicia sesión",
    auth_password_mismatch: "Las contraseñas no coinciden",
  },
  fr: {
    // Brand
    brand_name: "Voltea Ideas",
    brand_tagline: "Transformez les défis en idées structurées, modèles d'affaires et concepts prêts à présenter avec l'IA.",

    // Navigation
    nav_get_started: "Commencer",
    nav_login: "Connexion",
    nav_logout: "Déconnexion",
    nav_save: "Enregistrer",
    nav_home: "Accueil",
    nav_previous: "Précédent",
    nav_next: "Suivant",

    // Landing Page
    landing_hero_title: "Transformez les idées en",
    landing_hero_title_highlight: "Concepts",
    landing_hero_title_end: "Prêts à présenter",
    landing_hero_description: "Une plateforme d'innovation propulsée par l'IA qui vous emmène du problème à la présentation. Définissez vos défis, explorez des idées, construisez des modèles d'affaires et créez des pitchs convaincants — le tout dans un parcours guidé.",
    landing_start_innovating: "Commencer",
    landing_cta_title: "Prêt à lancer votre prochaine grande idée ?",
    landing_cta_description: "Rejoignez des entrepreneurs, des étudiants et des équipes d'innovation qui transforment leurs défis en concepts prêts à présenter grâce à l'IA.",
    landing_cta_button: "Commencer gratuitement",
    landing_footer_powered: "Propulsé par Emergent",

    // Steps
    step_describe: "Décrivez votre problème",
    step_explore: "Explorez les idées générées par l'IA",
    step_build: "Construisez votre modèle économique",
    step_pitch: "Présentez votre concept",

    // Dashboard
    dashboard_title: "Vos projets",
    dashboard_subtitle: "Transformez les défis en concepts prêts à présenter",
    dashboard_new_project: "Nouveau projet",
    dashboard_no_projects: "Aucun projet pour le moment",
    dashboard_no_projects_desc: "Commencez votre parcours d'innovation en créant votre premier projet. Définissez un problème et laissez l'IA vous aider à construire un concept prêt à présenter.",
    dashboard_create_first: "Créer votre premier projet",
    dashboard_delete: "Supprimer",
    dashboard_step: "Étape",

    // Dialog
    dialog_create_title: "Créer un nouveau projet",
    dialog_create_desc: "Donnez un nom à votre projet d'innovation pour commencer.",
    dialog_create_placeholder: "ex. Livraison alimentaire durable",
    dialog_cancel: "Annuler",
    dialog_create: "Créer un projet",

    // Wizard steps labels
    wizard_problem: "Problème",
    wizard_ideas: "Idées",
    wizard_concept: "Concept",
    wizard_value_prop: "Prop. de valeur",
    wizard_business_model: "Modèle éco.",
    wizard_prototype: "Prototype",
    wizard_pitch: "Pitch",

    // Problem Step
    problem_title: "Définissez votre défi",
    problem_description: "Décrivez le problème, le défi ou l'opportunité que vous souhaitez explorer. Soyez aussi précis que vous le souhaitez — l'IA vous aidera à le transformer en idées.",
    problem_tips_title: "Conseils pour de meilleurs résultats",
    problem_tip_1: "Commencez par \"Comment pourrions-nous...\" pour des défis ouverts",
    problem_tip_2: "Incluez le contexte des personnes concernées par ce problème",
    problem_tip_3: "Mentionnez les contraintes ou exigences éventuelles",
    problem_save_draft: "Enregistrer le brouillon",
    problem_generate: "Générer des idées",

    // Idea Map Step
    ideamap_title: "Carte d'idées",
    ideamap_description: "Explorez les idées générées par l'IA et sélectionnez-en une à développer",
    ideamap_generate: "Générer des idées",
    ideamap_regenerate: "Régénérer",
    ideamap_regenerate_hint: "La régénération créera 12 nouvelles idées alternatives",
    ideamap_continue: "Continuer avec l'idée",
    ideamap_ready_title: "Prêt à générer des idées",
    ideamap_ready_desc: "Cliquez sur \"Générer des idées\" pour que l'IA crée une carte conceptuelle à partir de votre problème.",
    ideamap_selected: "Idée sélectionnée",
    ideamap_generating: "Génération...",
    ideamap_likes: "likes",
    ideamap_favorite_hint: "Double-cliquez sur une idée pour la marquer comme favorite",

    // Concept Step
    concept_title: "Concept créatif",
    concept_description: "Votre concept prêt à présenter — la base de vos canevas",
    concept_generate: "Générer un concept",
    concept_regenerate: "Régénérer",
    concept_continue: "Continuer vers la proposition de valeur",
    concept_name: "Nom du concept",
    concept_problem_solved: "Problème résolu",
    concept_solution: "Solution proposée",
    concept_differentiation: "Différenciation clé",
    concept_audience: "Public cible",
    concept_benefits: "Bénéfices clés",

    // Value Proposition
    vpc_title: "Canvas de proposition de valeur",
    vpc_description: "Définissez les besoins clients et comment votre concept y répond",
    vpc_generate: "Générer le canvas",
    vpc_regenerate: "Régénérer",
    vpc_continue: "Continuer vers le modèle économique",
    vpc_customer_profile: "Profil client",
    vpc_value_map: "Carte de valeur",
    vpc_jobs: "Travaux du client",
    vpc_pains: "Douleurs",
    vpc_gains: "Gains",
    vpc_products: "Produits & services",
    vpc_pain_relievers: "Soulageurs de douleur",
    vpc_gain_creators: "Créateurs de gains",
    vpc_add_item: "Ajouter un élément...",

    // Business Model
    bmc_title: "Business Model Canvas",
    bmc_description: "Définissez comment votre concept crée, délivre et capture de la valeur",
    bmc_generate: "Générer le canvas",
    bmc_regenerate: "Régénérer",
    bmc_continue: "Créer le prompt de prototype",
    bmc_partners: "Partenaires clés",
    bmc_activities: "Activités clés",
    bmc_resources: "Ressources clés",
    bmc_value_props: "Propositions de valeur",
    bmc_relationships: "Relations clients",
    bmc_channels: "Canaux",
    bmc_segments: "Segments de clients",
    bmc_costs: "Structure de coûts",
    bmc_revenue: "Sources de revenus",

    // Prototype
    prototype_title: "Prompt de prototype",
    prototype_description: "Générez un prompt pour les outils de prototypage visuel",
    prototype_generate: "Générer le prompt",
    prototype_regenerate: "Régénérer",
    prototype_continue: "Créer la présentation",
    prototype_copy: "Copier",
    prototype_copied: "Copié !",
    prototype_context: "Contexte du produit",
    prototype_user: "Utilisateur cible",
    prototype_features: "Fonctionnalités clés",
    prototype_style: "Style visuel",
    prototype_tools: "Outils suggérés",

    // Presentation
    presentation_title: "Présentation & pitch",
    presentation_description: "Votre deck complet et votre elevator pitch",
    presentation_generate: "Générer la présentation",
    presentation_regenerate: "Régénérer",
    presentation_slides: "Diapositives de présentation",
    presentation_pitch: "Elevator pitch",
    presentation_pitch_duration: "~30-45 secondes à l'oral",
    presentation_complete_title: "Félicitations !",
    presentation_complete_desc: "Vous avez terminé votre parcours d'innovation. Partagez votre présentation et votre pitch avec les autres.",
    presentation_copied_slide: "Diapositive copiée dans le presse-papiers",
    completion_finalize_button: "Finaliser le projet",
    completion_finalized: "Projet enregistré avec succès",

    // Common
    common_loading: "Chargement...",
    common_saving: "Enregistrement...",
    common_creating: "Création...",
    common_generating: "Génération...",
    common_success: "Succès",
    common_error: "Erreur",
    common_add: "Ajouter",
    auth_login_title: "Connectez-vous à votre compte",
    auth_login_subtitle: "Utilisez e-mail et mot de passe ou continuez avec Google",
    auth_signup_title: "Créez votre compte",
    auth_signup_subtitle: "Commencez à transformer les défis en concepts prêts à présenter",
    auth_email: "E-mail",
    auth_password: "Mot de passe",
    auth_confirm_password: "Confirmez le mot de passe",
    auth_login_button: "Connexion",
    auth_signup_button: "Inscription",
    auth_login_with_google: "Continuer avec Google",
    auth_or: "ou",
    auth_no_account: "Pas encore de compte ?",
    auth_have_account: "Vous avez déjà un compte ?",
    auth_signup_link: "Inscrivez-vous",
    auth_login_link: "Connectez-vous",
    auth_password_mismatch: "Les mots de passe ne correspondent pas",
  },
  it: {
    // Brand
    brand_name: "Voltea Ideas",
    brand_tagline: "Trasforma le sfide in idee strutturate, modelli di business e concetti pronti per il pitch grazie all'IA.",

    // Navigation
    nav_get_started: "Inizia",
    nav_login: "Accedi",
    nav_logout: "Disconnetti",
    nav_save: "Salva",
    nav_home: "Home",
    nav_previous: "Precedente",
    nav_next: "Successivo",

    // Landing Page
    landing_hero_title: "Trasforma le idee in",
    landing_hero_title_highlight: "Concetti",
    landing_hero_title_end: "Pronti per il pitch",
    landing_hero_description: "Una piattaforma di innovazione basata sull'IA che ti porta dal problema alla presentazione. Definisci le sfide, esplora idee, costruisci modelli di business e crea pitch efficaci — tutto in un unico flusso guidato.",
    landing_start_innovating: "Inizia",
    landing_cta_title: "Pronto a lanciare la tua prossima grande idea?",
    landing_cta_description: "Unisciti a imprenditori, studenti e team di innovazione che trasformano le loro sfide in concetti pronti per il pitch grazie all'IA.",
    landing_cta_button: "Inizia gratis",
    landing_footer_powered: "Sviluppato da Emergent",

    // Steps
    step_describe: "Descrivi il tuo problema",
    step_explore: "Esplora le idee generate dall'IA",
    step_build: "Costruisci il tuo modello di business",
    step_pitch: "Presenta il tuo concetto",

    // Dashboard
    dashboard_title: "I tuoi progetti",
    dashboard_subtitle: "Trasforma le sfide in concetti pronti per il pitch",
    dashboard_new_project: "Nuovo progetto",
    dashboard_no_projects: "Ancora nessun progetto",
    dashboard_no_projects_desc: "Inizia il tuo percorso di innovazione creando il tuo primo progetto. Definisci un problema e lascia che l'IA ti aiuti a costruire un concetto pronto per il pitch.",
    dashboard_create_first: "Crea il tuo primo progetto",
    dashboard_delete: "Elimina",
    dashboard_step: "Passo",

    // Dialog
    dialog_create_title: "Crea un nuovo progetto",
    dialog_create_desc: "Dai un nome al tuo progetto di innovazione per iniziare.",
    dialog_create_placeholder: "es. Consegna di cibo sostenibile",
    dialog_cancel: "Annulla",
    dialog_create: "Crea progetto",

    // Wizard labels
    wizard_problem: "Problema",
    wizard_ideas: "Idee",
    wizard_concept: "Concetto",
    wizard_value_prop: "Prop. di valore",
    wizard_business_model: "Modello di business",
    wizard_prototype: "Prototype",
    wizard_pitch: "Pitch",

    // Problem Step
    problem_title: "Definisci la tua sfida",
    problem_description: "Descrivi il problema, la sfida o l'opportunità che vuoi esplorare. Sii dettagliato quanto vuoi — l'IA ti aiuterà a trasformarla in idee.",
    problem_tips_title: "Suggerimenti per risultati migliori",
    problem_tip_1: "Inizia con \"Come potremmo...\" per sfide aperte",
    problem_tip_2: "Includi il contesto di chi vive questo problema",
    problem_tip_3: "Indica eventuali vincoli o requisiti",
    problem_save_draft: "Salva bozza",
    problem_generate: "Genera idee",

    // Idea Map
    ideamap_title: "Mappa delle idee",
    ideamap_description: "Esplora le idee generate dall'IA e selezionane una da sviluppare",
    ideamap_generate: "Genera idee",
    ideamap_regenerate: "Rigenera",
    ideamap_regenerate_hint: "La rigenerazione creerà 12 nuove idee alternative",
    ideamap_continue: "Continua con l'idea",
    ideamap_ready_title: "Pronto a generare idee",
    ideamap_ready_desc: "Fai clic su \"Genera idee\" per permettere all'IA di creare una mappa concettuale a partire dal tuo problema.",
    ideamap_selected: "Idea selezionata",
    ideamap_generating: "Generazione...",
    ideamap_likes: "mi piace",
    ideamap_favorite_hint: "Fai doppio clic su un'idea per segnarla come preferita",

    // Concept
    concept_title: "Concetto creativo",
    concept_description: "Il tuo concetto pronto per il pitch — la base dei tuoi canvas",
    concept_generate: "Genera concetto",
    concept_regenerate: "Rigenera",
    concept_continue: "Continua alla proposta di valore",
    concept_name: "Nome del concetto",
    concept_problem_solved: "Problema risolto",
    concept_solution: "Soluzione proposta",
    concept_differentiation: "Differenziazione chiave",
    concept_audience: "Pubblico target",
    concept_benefits: "Benefici chiave",

    // Value Proposition
    vpc_title: "Canvas proposta di valore",
    vpc_description: "Definisci i bisogni dei clienti e come il tuo concetto li soddisfa",
    vpc_generate: "Genera canvas",
    vpc_regenerate: "Rigenera",
    vpc_continue: "Continua al modello di business",
    vpc_customer_profile: "Profilo cliente",
    vpc_value_map: "Mappa del valore",
    vpc_jobs: "Compiti del cliente",
    vpc_pains: "Dolori",
    vpc_gains: "Guadagni",
    vpc_products: "Prodotti & servizi",
    vpc_pain_relievers: "Alleviatori di dolore",
    vpc_gain_creators: "Creatori di guadagno",
    vpc_add_item: "Aggiungi elemento...",

    // Business Model
    bmc_title: "Business Model Canvas",
    bmc_description: "Definisci come il tuo concetto crea, offre e cattura valore",
    bmc_generate: "Genera canvas",
    bmc_regenerate: "Rigenera",
    bmc_continue: "Crea il prompt di prototipo",
    bmc_partners: "Partner chiave",
    bmc_activities: "Attività chiave",
    bmc_resources: "Risorse chiave",
    bmc_value_props: "Proposte di valore",
    bmc_relationships: "Relazioni con i clienti",
    bmc_channels: "Canali",
    bmc_segments: "Segmenti di clienti",
    bmc_costs: "Struttura dei costi",
    bmc_revenue: "Flussi di ricavi",

    // Prototype
    prototype_title: "Prompt di prototipo",
    prototype_description: "Genera un prompt per strumenti di prototipazione visiva",
    prototype_generate: "Genera prompt",
    prototype_regenerate: "Rigenera",
    prototype_continue: "Crea presentazione",
    prototype_copy: "Copia",
    prototype_copied: "Copiato!",
    prototype_context: "Contesto del prodotto",
    prototype_user: "Utente target",
    prototype_features: "Funzionalità chiave",
    prototype_style: "Stile visivo",
    prototype_tools: "Strumenti suggeriti",

    // Presentation
    presentation_title: "Presentazione & pitch",
    presentation_description: "Il tuo pitch deck completo e l'elevator pitch",
    presentation_generate: "Genera presentazione",
    presentation_regenerate: "Rigenera",
    presentation_slides: "Slide della presentazione",
    presentation_pitch: "Elevator pitch",
    presentation_pitch_duration: "~30-45 secondi quando parlato",
    presentation_complete_title: "Congratulazioni!",
    presentation_complete_desc: "Hai completato il tuo percorso di innovazione. Condividi la tua presentazione e il tuo pitch con gli altri.",
    presentation_copied_slide: "Slide copiata negli appunti",
    completion_finalize_button: "Finalizza progetto",
    completion_finalized: "Progetto salvato con successo",

    // Common
    common_loading: "Caricamento...",
    common_saving: "Salvataggio...",
    common_creating: "Creazione...",
    common_generating: "Generazione...",
    common_success: "Successo",
    common_error: "Errore",
    common_add: "Aggiungi",
    auth_login_title: "Accedi al tuo account",
    auth_login_subtitle: "Usa e-mail e password oppure continua con Google",
    auth_signup_title: "Crea il tuo account",
    auth_signup_subtitle: "Inizia a trasformare le sfide in concetti pronti per il pitch",
    auth_email: "E-mail",
    auth_password: "Password",
    auth_confirm_password: "Conferma password",
    auth_login_button: "Accedi",
    auth_signup_button: "Registrati",
    auth_login_with_google: "Continua con Google",
    auth_or: "oppure",
    auth_no_account: "Non hai un account?",
    auth_have_account: "Hai già un account?",
    auth_signup_link: "Registrati",
    auth_login_link: "Accedi",
    auth_password_mismatch: "Le password non corrispondono",
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
